/**
 * Expense Form Validators
 * Centralized validation logic for expense forms
 */

/**
 * Validate expense description
 * @param {string} description - Expense description
 * @returns {boolean} True if valid
 */
export const validateDescription = (description) => {
  return description && description.trim().length > 0;
};

/**
 * Validate expense date
 * @param {Date|null} date - Expense date
 * @returns {boolean} True if valid
 */
export const validateDate = (date) => {
  return date !== null && date !== undefined;
};

/**
 * Validate expense category
 * @param {string} category - Expense category ID
 * @returns {boolean} True if valid
 */
export const validateCategory = (category) => {
  return category && category.trim().length > 0;
};

/**
 * Validate expense currency
 * @param {string} currency - Currency code
 * @returns {boolean} True if valid
 */
export const validateCurrency = (currency) => {
  return currency && currency.trim().length > 0;
};

/**
 * Validate sub total amount
 * @param {string|number} subTotal - Sub total amount
 * @returns {boolean} True if valid
 */
export const validateSubTotal = (subTotal) => {
  if (!subTotal || subTotal.toString().trim() === "") {
    return false;
  }
  const numValue = parseFloat(subTotal);
  return !isNaN(numValue) && numValue >= 0;
};

/**
 * Validate tax amount
 * @param {string|number} tax - Tax amount
 * @returns {boolean} True if valid
 */
export const validateTax = (tax) => {
  if (tax === null || tax === undefined || tax.toString().trim() === "") {
    return true; // Tax is optional
  }
  const numValue = parseFloat(tax);
  return !isNaN(numValue) && numValue >= 0;
};

/**
 * Validate expense user
 * @param {string} user - User ID
 * @returns {boolean} True if valid
 */
export const validateUser = (user) => {
  return user && user.trim().length > 0;
};

/**
 * Validate expense client
 * @param {string} client - Client ID
 * @returns {boolean} True if valid
 */
export const validateClient = (client) => {
  return client && client.trim().length > 0;
};

/**
 * Validate expense company
 * @param {string} company - Company ID
 * @returns {boolean} True if valid
 */
export const validateCompany = (company) => {
  return company && company.trim().length > 0;
};

/**
 * Validate file attachment
 * @param {Array} files - Array of file objects
 * @returns {Object} Validation result with isValid and error
 */
export const validateFileAttachment = (files) => {
  if (!files || files.length === 0) {
    return { isValid: true, error: null }; // Files are optional
  }
  
  // Check file count limit (if needed)
  const MAX_FILES = 10;
  if (files.length > MAX_FILES) {
    return {
      isValid: false,
      error: `Maximum ${MAX_FILES} files allowed`,
    };
  }
  
  // Check file sizes (if needed)
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`,
      };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate complete expense form
 * @param {Object} formData - Form data object
 * @returns {Object} Validation result with isValid and errors array
 */
export const validateExpenseForm = (formData) => {
  const errors = [];
  
  if (!validateDescription(formData.description)) {
    errors.push("description");
  }
  
  if (!validateDate(formData.expenseDate)) {
    errors.push("date");
  }
  
  if (!validateCategory(formData.category)) {
    errors.push("category");
  }
  
  if (!validateCurrency(formData.currency)) {
    errors.push("currency");
  }
  
  if (!validateSubTotal(formData.subTotal)) {
    errors.push("subTotal");
  }
  
  if (!validateTax(formData.tax)) {
    errors.push("tax");
  }
  
  if (!validateUser(formData.user)) {
    errors.push("user");
  }
  
  if (!validateClient(formData.client)) {
    errors.push("client");
  }
  
  if (!validateCompany(formData.company)) {
    errors.push("company");
  }
  
  const fileValidation = validateFileAttachment(formData.fileAttachment);
  if (!fileValidation.isValid) {
    errors.push("fileAttachment");
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};
